/**
 * Little books: short stories she reads herself, written by hand.
 *
 * Each is a story engine config: every word is one she can sound out at the
 * book's level or knows by sight (the story engine's validator checks, and a
 * test runs it over every book), and every scene is drawn from the word
 * bank's clay pictures (a fourth thing in a scene is drawn in the sky, so
 * it is only ever the sun, the moon, a star or rain). A few per blending
 * level, so the day she reaches a level there is a new book waiting that
 * she can actually read. Each has a
 * tiny problem and a happy end, about things a small child loves: mud,
 * baths, a bus, a frog with a drum, the moon.
 */

import { SIGHT_WORDS } from './phonics'

export interface LittleBook {
  /** Stable, used in the reading path's step id (book-<id>). */
  id: string
  /** The story engine config. */
  config: {
    title: string
    level: 1 | 2 | 3 | 4 | 5
    listener: string
    cover: string
    names: string[]
    pages: Array<{ text: string; scene: string[]; place?: 'grass' | 'mud' | 'sand' | 'snow' | 'sea' | 'night' | 'home' | 'space'; rel?: 'on' | 'in' }>
  }
}

export const LITTLE_BOOKS: LittleBook[] = [
  // 1 · held sounds: words that start with a sound she can stretch, and
  // only the first sight set (the, a, is, no), the level she stays on longest
  { id: 'rat-in-the-mud', config: { title: 'Rat in the Mud', level: 1, listener: '🧸', cover: 'rat', names: [], pages: [
    { text: 'A rat sat in the mud.', scene: ['rat'], place: 'mud' },
    { text: 'Mud on the rat! Mud, mud, mud.', scene: ['rat'], place: 'mud' },
    { text: 'A mop! Mop, rat, mop.', scene: ['rat', 'mop'], place: 'home' },
    { text: 'Mop, mop, mop!', scene: ['mop', 'rat'], place: 'home' },
    { text: 'The rat is on a rug. No mud!', scene: ['rat', 'rug'], place: 'home', rel: 'on' },
  ] } },
  // Sam is a dog in every picture; his name is a word she can sound out.
  { id: 'fun-in-the-sun', config: { title: 'Fun in the Sun', level: 1, listener: '🧸', cover: 'dog', names: ['Sam'], pages: [
    { text: 'The sun is up.', scene: ['sun'], place: 'grass' },
    { text: 'Sam sat on a mat in the sun.', scene: ['dog', 'mat', 'log', 'sun'], place: 'grass', rel: 'on' },
    { text: 'Sun on Sam! Sam is sad. No nap.', scene: ['dog', 'mat', 'log', 'sun'], place: 'grass' },
    { text: 'A fan! Fan, fan, fan.', scene: ['dog', 'fan', 'mat'], place: 'grass' },
    { text: 'A mug! Sip, sip, sip.', scene: ['dog', 'mug', 'mat'], place: 'grass' },
    { text: 'Nap, Sam, nap. Fun in the sun!', scene: ['dog', 'mat', 'fan', 'sun'], place: 'grass' },
  ] } },
  { id: 'rat-and-fig', config: { title: 'The Rat and the Fig', level: 1, listener: '🐶', cover: 'fig', names: [], pages: [
    { text: 'A rat sat in the sun.', scene: ['rat', 'log', 'fig', 'sun'], place: 'grass' },
    { text: 'A fig! It is up on a log.', scene: ['fig', 'log', 'rat'], place: 'grass', rel: 'on' },
    { text: 'Up, up, up the log.', scene: ['rat', 'log', 'fig'], place: 'grass' },
    { text: 'No! The rat is in the mud.', scene: ['rat', 'log'], place: 'mud' },
    { text: 'Up, up, up! The rat is on the log.', scene: ['rat', 'log', 'fig'], place: 'grass', rel: 'on' },
    { text: 'Num, num, num! Nap, rat, nap.', scene: ['rat', 'log'], place: 'grass' },
  ] } },
  // Hide and seek: Mom is found at last, in clay like everyone on the shelf.
  { id: 'is-mom-in-the-van', config: { title: 'Is Mom in the Van?', level: 1, listener: '🧸', cover: 'van', names: ['Sam', 'Mom'], pages: [
    { text: 'Is Mom on the rug? No.', scene: ['dog', 'rug'], place: 'home' },
    { text: 'Is Mom in the van? No.', scene: ['dog', 'van'], place: 'grass' },
    { text: 'Is Mom on the log? No.', scene: ['dog', 'log'], place: 'grass' },
    { text: 'Is Mom in the mud? No!', scene: ['dog'], place: 'mud' },
    { text: 'Mom! Mom is in the sun.', scene: ['dog', 'mom', 'mat', 'sun'], place: 'grass' },
    { text: "Sam sat on Mom's lap. Fun!", scene: ['dog', 'mom'], place: 'grass', rel: 'on' },
  ] } },

  // 2 · quick first sounds: the vowel held and the quick sound joined on
  // A small rescue: the pig is stuck, the cat helps, and the big pig is a bed.
  { id: 'big-pig', config: { title: 'The Big Pig', level: 2, listener: '🧸', cover: 'pig', names: [], pages: [
    { text: 'A big pig is in the mud.', scene: ['pig'], place: 'mud' },
    { text: 'The pig can not get up!', scene: ['pig'], place: 'mud' },
    { text: 'A cat can tug. Tug, cat, tug!', scene: ['cat', 'pig'], place: 'mud' },
    { text: 'Up, pig, up! The pig is up.', scene: ['pig', 'cat'], place: 'grass' },
    { text: 'The pig is wet. The cat is wet.', scene: ['pig', 'cat'], place: 'grass' },
    { text: 'A cat sat on the big pig. Nap, nap, nap.', scene: ['cat', 'pig'], place: 'grass', rel: 'on' },
  ] } },
  { id: 'hen-and-bug', config: { title: 'The Hen and the Bug', level: 2, listener: '🐱', cover: 'hen', names: [], pages: [
    { text: 'A hen sat in the sun.', scene: ['hen', 'nest', 'log', 'sun'], place: 'grass' },
    { text: 'A bug is on a log.', scene: ['bug', 'log'], place: 'grass', rel: 'on' },
    { text: 'The hen can see the bug.', scene: ['hen', 'bug', 'log'], place: 'grass' },
    { text: 'The bug hid in the log.', scene: ['bug', 'log'], place: 'grass', rel: 'in' },
    { text: 'The hen did not get it.', scene: ['hen', 'log'], place: 'grass' },
    { text: 'The hen and the bug sit in the sun.', scene: ['hen', 'bug', 'log', 'sun'], place: 'grass' },
  ] } },
  { id: 'big-bus', config: { title: 'The Big Bus', level: 2, listener: '🧸', cover: 'bus', names: [], pages: [
    { text: 'Get on the bus!', scene: ['bus'], place: 'grass' },
    { text: 'A dog got on the bus.', scene: ['dog', 'bus'], place: 'grass', rel: 'in' },
    { text: 'A cat got on the bus.', scene: ['cat', 'bus'], place: 'grass', rel: 'in' },
    { text: 'A big pig got on the bus.', scene: ['pig', 'bus'], place: 'grass', rel: 'in' },
    { text: 'All on the bus!', scene: ['dog', 'bus', 'pig', 'sun'], place: 'grass', rel: 'in' },
    { text: 'Off we go!', scene: ['bus', 'dog', 'cat', 'sun'], place: 'grass' },
  ] } },

  // 3 · two letters, one sound: sh, ch, th, ck, ng
  // The duck stays in the bath all through: each new toy joins it, then the child gets in.
  { id: 'duck-in-the-bath', config: { title: 'The Duck in the Bath', level: 3, listener: '🧸', cover: 'duck', names: [], pages: [
    { text: 'Run the bath!', scene: ['bath'], place: 'home' },
    { text: 'A duck is in the bath.', scene: ['duck', 'bath'], place: 'home', rel: 'in' },
    { text: 'A ship is in the bath.', scene: ['ship', 'bath', 'duck'], place: 'home', rel: 'in' },
    { text: 'A fish is in the bath!', scene: ['fish', 'bath', 'duck'], place: 'home', rel: 'in' },
    { text: 'Then in I get. Such fun!', scene: ['kid', 'bath', 'duck'], place: 'home', rel: 'in' },
    { text: 'Rub a dub dub. Then bed.', scene: ['bed', 'duck'], place: 'home' },
  ] } },
  { id: 'king-and-ring', config: { title: 'The King and the Ring', level: 3, listener: '🐶', cover: 'king', names: [], pages: [
    { text: 'A king had a ring.', scene: ['king', 'ring'], place: 'home' },
    { text: 'The ring fell in a dish.', scene: ['ring', 'dish'], place: 'home', rel: 'in' },
    { text: 'A chick got the ring.', scene: ['chick', 'ring'], place: 'home' },
    { text: 'The chick ran and ran.', scene: ['chick', 'ring'], place: 'grass' },
    { text: 'Is this the king\'s ring?', scene: ['chick', 'king', 'ring'], place: 'home' },
    { text: 'The king sang and sang.', scene: ['king', 'chick', '🎶'], place: 'home' },
  ] } },

  // 4 · sounds side by side: fr, dr, st, mp, nd
  { id: 'frog-and-drum', config: { title: 'The Frog and the Drum', level: 4, listener: '🐱', cover: 'frog', names: [], pages: [
    { text: 'A frog sits on a log.', scene: ['frog', 'log'], place: 'grass', rel: 'on' },
    { text: 'The frog has a drum.', scene: ['frog', 'drum'], place: 'grass' },
    { text: 'Tap, tap, tap. The frog is glad.', scene: ['frog', 'drum'], place: 'grass' },
    { text: 'A crab and an ant stop and clap.', scene: ['crab', 'ant', 'clap'], place: 'grass' },
    { text: 'The frog can drum and sing!', scene: ['frog', 'drum', 'crab'], place: 'grass' },
    { text: 'The best band in the land.', scene: ['frog', 'crab', 'ant', 'sun'], place: 'grass' },
  ] } },
  { id: 'sled-hill', config: { title: 'The Sled on the Hill', level: 4, listener: '🧸', cover: 'sled', names: [], pages: [
    { text: 'Get the sled!', scene: ['sled'], place: 'snow' },
    { text: 'Up, up, up the hill.', scene: ['kid', 'sled'], place: 'snow' },
    { text: 'The kid sits on the sled.', scene: ['kid', 'sled'], place: 'snow', rel: 'on' },
    { text: 'Swish! Fast, fast, fast!', scene: ['sled', 'kid'], place: 'snow' },
    { text: 'Bump! The kid and the sled stop.', scene: ['kid', 'sled'], place: 'snow' },
    { text: 'The kid grins and runs up the hill.', scene: ['kid', 'sled'], place: 'snow' },
  ] } },

  // 5 · vowel teams: oo, ee, ai, oa, ar, or, igh, ow
  { id: 'owl-and-moon', config: { title: 'The Owl and the Moon', level: 5, listener: '🧸', cover: 'owl', names: [], pages: [
    { text: 'The moon is up. A star is up.', scene: ['moon', 'owl', 'tree', 'star'], place: 'night' },
    { text: 'An owl is up too. Hoot!', scene: ['owl', 'tree', 'nest', 'moon'], place: 'night' },
    { text: 'Hoot, hoot, said the owl.', scene: ['owl', 'tree', 'nest', 'star'], place: 'night' },
    { text: 'A cow sees the moon.', scene: ['cow', 'tree', 'owl', 'moon'], place: 'night' },
    { text: 'A sheep and a goat look up too.', scene: ['sheep', 'goat', 'cow', 'moon'], place: 'night' },
    { text: 'Hush, owl. Sleep tight, moon.', scene: ['owl', 'tree', 'nest', 'moon'], place: 'night' },
  ] } },
  { id: 'farm-train', config: { title: 'The Farm Train', level: 5, listener: '🐶', cover: 'train', names: [], pages: [
    { text: 'Toot, toot! Here is the train.', scene: ['train'], place: 'grass' },
    { text: 'A goat gets on the train.', scene: ['goat', 'train'], place: 'grass', rel: 'in' },
    { text: 'A sheep and a cow get on.', scene: ['sheep', 'cow', 'train'], place: 'grass' },
    { text: 'The train zooms far.', scene: ['train', 'tree', 'goat', 'sun'], place: 'grass' },
    { text: 'Rain! The train is wet.', scene: ['train', 'goat', 'cow', 'rain'], place: 'grass' },
    { text: 'Then the sun is back. The farm!', scene: ['train', 'goat', 'cow', 'sun'], place: 'grass' },
  ] } },
  { id: 'snail-and-shark', config: { title: 'The Snail and the Shark', level: 5, listener: '🐱', cover: 'snail', names: [], pages: [
    { text: 'A snail sat on a rock.', scene: ['snail', 'rock'], place: 'sand', rel: 'on' },
    { text: 'The snail had a boat.', scene: ['snail', 'boat'], place: 'sea' },
    { text: 'The boat went far, far, far.', scene: ['boat', 'snail'], place: 'sea' },
    { text: 'A big shark swam up to the snail.', scene: ['shark', 'snail', 'boat'], place: 'sea' },
    { text: 'The shark and the snail are pals.', scene: ['shark', 'snail'], place: 'sea' },
    { text: 'Then the snail went back to his rock.', scene: ['snail', 'rock', 'shell', 'sun'], place: 'sand' },
  ] } },
]

/**
 * Who each named character is across her books, read from the pages
 * themselves: on a page that names them, they are drawn first (the main
 * character leads every scene), unless the book draws the name as its own
 * picture (Mom is drawn as mom). A name drawn as two different things is a
 * shelf that no longer reads as one world.
 */
export function shelfCast(books: Array<{ title: string; config: { names?: unknown; pages?: unknown } }>): Array<{ name: string; drawn_as: string[]; books: string[]; as_in: Record<string, string[]> }> {
  const cast = new Map<string, Map<string, Set<string>>>()
  for (const book of books) {
    const names = Array.isArray(book.config.names) ? book.config.names.filter((n): n is string => typeof n === 'string') : []
    const pages = Array.isArray(book.config.pages) ? (book.config.pages as Array<{ text?: unknown; scene?: unknown }>) : []
    const sceneOf = (page: { scene?: unknown }) => (Array.isArray(page.scene) ? page.scene.filter((x): x is string => typeof x === 'string') : [])
    for (const name of names) {
      const named = new RegExp(`\\b${name.replace(/[^A-Za-z']/g, '')}\\b`)
      // A name with its own picture (Mom) is that picture, and a page that names her without it is someone looking for her.
      const self = (x: string) => x.toLowerCase() === name.toLowerCase()
      const pictured = pages.some((p) => sceneOf(p).some(self))
      for (const page of pages) {
        const scene = sceneOf(page)
        const drawn = pictured ? scene.find(self) : scene[0]
        if (typeof page.text !== 'string' || !named.test(page.text) || !drawn) continue
        const byBook = cast.get(name) ?? new Map<string, Set<string>>()
        byBook.set(book.title, (byBook.get(book.title) ?? new Set()).add(drawn))
        cast.set(name, byBook)
      }
    }
  }
  return [...cast].map(([name, byBook]) => ({
    name,
    drawn_as: [...new Set([...byBook.values()].flatMap((d) => [...d]))],
    books: [...byBook.keys()],
    as_in: Object.fromEntries([...byBook].map(([title, d]) => [title, [...d]])),
  }))
}

/**
 * Why a book rewritten for her breaks her shelf, or nothing: a named
 * character is a fact of the shelf, so a name her other books draw as one
 * thing cannot be drawn as another in this one. `shelf` is every book as
 * it is for her, this one included.
 */
export function castProblems(shelf: Array<{ title: string; config: { names?: unknown; pages?: unknown } }>, title: string): string[] {
  return shelfCast(shelf).flatMap((c) => {
    const here = c.as_in[title]
    const elsewhere = Object.entries(c.as_in).filter(([t]) => t !== title)
    const others = [...new Set(elsewhere.flatMap(([, d]) => d))]
    if (!here || !others.length || here.every((d) => others.includes(d))) return []
    const where = elsewhere.filter(([, d]) => d.some((x) => others.includes(x))).map(([t]) => t)
    return [`${c.name} is a ${others.join(' and ')} on her shelf (in ${where.join(' and ')}), but this book draws ${c.name} as a ${here.filter((d) => !others.includes(d)).join(' and ')}; draw ${c.name} as a ${others[0]}, or give this character a name no other book uses`]
  })
}

const SIGHT = new Map(SIGHT_WORDS.map((w) => [w.toLowerCase(), w]))

/** The sight words a book uses (title and pages), in the order it uses them. */
export function bookSightWords(config: { title: string; pages: Array<{ text: string }> }): string[] {
  const out: string[] = []
  for (const text of [config.title, ...config.pages.map((p) => p.text)]) {
    for (const raw of text.split(/[^A-Za-z']+/)) {
      const w = SIGHT.get(raw.toLowerCase().replace(/'s$/, ''))
      if (w && !out.includes(w)) out.push(w)
    }
  }
  return out
}

/**
 * The sight words in small sets, in the order the little books first use
 * them (the level 1 books' words first), then the rest. So the sets she
 * meets first are the words the books she can open next need. The reading
 * path's sight steps and the Sight words deck are both these sets.
 */
export const SIGHT_SETS: string[][] = (() => {
  const order: string[] = []
  for (const b of [...LITTLE_BOOKS].sort((x, y) => x.config.level - y.config.level)) {
    for (const w of bookSightWords(b.config)) if (!order.includes(w)) order.push(w)
  }
  for (const w of SIGHT_WORDS) if (!order.includes(w)) order.push(w)
  // Sets of four, the last few of three, so no set is a lone word.
  const count = Math.ceil(order.length / 4)
  const sizes = Array.from({ length: count }, (_, i) => Math.floor(order.length / count) + (i < order.length % count ? 1 : 0))
  return sizes.map((n, i) => order.slice(sizes.slice(0, i).reduce((x, y) => x + y, 0)).slice(0, n))
})()

/**
 * Letter sounds in small sets, in the order that makes words soonest: s a t
 * m reads sat and mat, adding p i n o reads map, man and mop, and by the
 * fourth set nearly every level 1 word sounds out from sounds she has met (the
 * order sound-to-blend programmes use, after SATPIN). The quick sounds that
 * end most level 1 words (t, p, d, g) come early for that reason, not after
 * the sounds she can stretch. Then the new first sounds of level 2 (c-a-t,
 * b-u-s). `level` is the blending level each set serves, and a set opens by
 * it (soundSetOpens), not by the set before. Two-letter sounds (sh, ee) are
 * in the Two-letter sounds deck, and Blending itself keeps teaching every
 * sound as she slides.
 */
export const SOUND_SETS: Array<{ letters: string[]; level: number }> = [
  { letters: ['s', 'a', 't', 'm'], level: 1 },
  { letters: ['p', 'i', 'n', 'o'], level: 1 },
  { letters: ['d', 'g', 'u', 'r'], level: 1 },
  { letters: ['f', 'l', 'e', 'v'], level: 1 },
  { letters: ['c', 'b', 'h', 'j'], level: 2 },
  { letters: ['w', 'z'], level: 2 },
]
